import React from "react";
import styled from "styled-components";
import { colors } from "../theme/colors";
import { shadows } from "../theme/shadows";

const SegmentedPicker = ({
  options = [],
  activeKey,
  onSelect,
  size = "medium",
  variant = "default",
  className,
  ...props
}) => {
  return (
    <Container className={className} size={size} {...props}>
      <PickerWrapper variant={variant}>
        {options.map((option) => (
          <SegmentButton
            key={option.eventKey}
            active={activeKey === option.eventKey}
            onClick={() => onSelect(option.eventKey)}
            size={size}
            variant={variant}
          >
            {option.label}
          </SegmentButton>
        ))}
      </PickerWrapper>
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  padding: ${({ size }) => {
    switch (size) {
      case "small":
        return "8px";
      case "large":
        return "16px";
      default:
        return "12px";
    }
  }};
  contain: layout;
  transform: translateZ(0);
  overflow-x: auto;
  overflow-y: hidden;
  -webkit-overflow-scrolling: touch; /* Smooth scrolling on iOS */
  overscroll-behavior-x: contain; /* Prevent scroll chaining */

  /* Hide scrollbar but keep functionality */
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* IE and Edge */

  &::-webkit-scrollbar {
    display: none; /* Chrome, Safari, Opera */
  }

  /* On mobile, ensure scrolling works properly */
  @media screen and (max-width: 768px) {
    justify-content: flex-start;
    /* Force hardware acceleration for smoother scrolling */
    will-change: scroll-position;
  }
`;

const PickerWrapper = styled.div`
  display: inline-flex; /* Changed from flex to inline-flex for better fit-content behavior */
  background: ${({ variant }) => {
    switch (variant) {
      case "glass":
        return "rgba(255, 255, 255, 0.1)";
      case "subtle":
        return colors.neutral[100];
      default:
        return colors.neutral[200];
    }
  }};
  border-radius: ${({ variant }) => (variant === "subtle" ? "25px" : "32px")};
  padding: 4px;
  box-shadow: ${({ variant }) => {
    switch (variant) {
      case "glass":
        return "inset 0 1px 2px rgba(0, 0, 0, 0.1), 0 4px 12px rgba(0, 0, 0, 0.05)";
      case "subtle":
        return shadows.sm;
      default:
        return shadows.md;
    }
  }};
  backdrop-filter: ${({ variant }) =>
    variant === "glass" ? "blur(12px)" : "none"};
  border: ${({ variant }) => {
    switch (variant) {
      case "glass":
        return "1px solid rgba(255, 255, 255, 0.2)";
      case "subtle":
        return "1px solid " + colors.neutral[300];
      default:
        return "1px solid " + colors.neutral[300];
    }
  }};
  gap: 2px;
  position: relative;
  overflow: visible; /* Changed from hidden to allow scrolling */
  width: auto; /* Changed from fit-content for better mobile behavior */
  max-width: none; /* Remove max-width constraint */
  min-width: 0;
  transform: translateZ(0);
  backface-visibility: hidden;
  flex-shrink: 0; /* Prevent wrapper from shrinking */
`;

const SegmentButton = styled.button`
  flex: 0 0 auto; /* Don't grow or shrink, use natural size */
  border: none;
  background: ${({ active, variant }) => {
    if (active) {
      switch (variant) {
        case "glass":
          return "rgba(255, 255, 255, 0.9)";
        case "subtle":
          return "#ffffff";
        default:
          return "#ffffff";
      }
    }
    return "transparent";
  }};
  color: ${({ active, variant }) => {
    if (active) {
      switch (variant) {
        case "glass":
          return colors.primary.main;
        case "subtle":
          return colors.primary.main;
        default:
          return colors.primary.main;
      }
    }
    return colors.neutral[600];
  }};
  border-radius: ${({ variant }) => (variant === "subtle" ? "20px" : "28px")};
  padding: ${({ size }) => {
    switch (size) {
      case "small":
        return "8px 16px";
      case "large":
        return "16px 32px";
      default:
        return "12px 24px";
    }
  }};
  font-size: ${({ size }) => {
    switch (size) {
      case "small":
        return "14px";
      case "large":
        return "18px";
      default:
        return "16px";
    }
  }};
  font-weight: ${({ active }) => (active ? "600" : "500")};
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
  white-space: nowrap;
  min-width: fit-content;
  position: relative;
  z-index: 1;
  will-change: transform, background-color, color, box-shadow;
  touch-action: manipulation; /* Improve touch responsiveness */

  /* iPadOS-style shadow for active state */
  box-shadow: ${({ active, variant }) => {
    if (active) {
      switch (variant) {
        case "glass":
          return "0 2px 8px rgba(0, 0, 0, 0.1), 0 1px 3px rgba(0, 0, 0, 0.08)";
        case "subtle":
          return shadows.sm;
        default:
          return shadows.sm;
      }
    }
    return "none";
  }};

  &:hover:not(:disabled) {
    background: ${({ active, variant }) => {
      if (active) {
        return "inherit";
      }
      switch (variant) {
        case "glass":
          return "rgba(255, 255, 255, 0.15)";
        case "subtle":
          return "rgba(44, 90, 160, 0.05)";
        default:
          return "rgba(44, 90, 160, 0.05)";
      }
    }};
    color: ${({ active }) => (active ? "inherit" : colors.primary.main)};
  }

  &:active {
    transform: ${({ active }) => (active ? "none" : "scale(0.98)")};
  }

  &:focus {
    outline: none;
    box-shadow: ${({ active, variant }) => {
      const baseShadow = active
        ? variant === "glass"
          ? "0 2px 8px rgba(0, 0, 0, 0.1), 0 1px 3px rgba(0, 0, 0, 0.08)"
          : shadows.sm
        : "none";
      return `${baseShadow}, 0 0 0 3px rgba(44, 90, 160, 0.2)`;
    }};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  /* Responsive font sizing */
  @media screen and (max-width: 768px) {
    font-size: ${({ size }) => {
      switch (size) {
        case "small":
          return "12px";
        case "large":
          return "16px";
        default:
          return "14px";
      }
    }};
    padding: ${({ size }) => {
      switch (size) {
        case "small":
          return "6px 12px";
        case "large":
          return "12px 18px";
        default:
          return "10px 16px";
      }
    }};
  }
`;

export default SegmentedPicker;
